// frontend/src/components/CampaignManager/dialogs/WerbetraegerDialog.tsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { Werbetraeger } from '../types';

interface Props {
  open: boolean;
  werbetraeger: Werbetraeger | null;
  onClose: () => void;
  onSave: (werbetraeger: Partial<Werbetraeger>, id?: number) => Promise<void>;
}

export default function WerbetraegerDialog({ open, werbetraeger, onClose, onSave }: Props) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [form, setForm] = useState({
    bezeichnung: '',
    kurz: '',
    bemerkungen: '',
    active: true
  });

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (werbetraeger) {
      setForm({
        bezeichnung: werbetraeger.bezeichnung,
        kurz: werbetraeger.kurz,
        bemerkungen: werbetraeger.bemerkungen || '',
        active: werbetraeger.active
      });
    } else {
      setForm({
        bezeichnung: '',
        kurz: '',
        bemerkungen: '',
        active: true
      });
    }
  }, [werbetraeger, open]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(form, werbetraeger?.id);
      onClose();
    } catch (error) {
      console.error('Error saving werbetraeger:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => !saving && onClose()}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle>
        {werbetraeger ? 'Werbetraeger bearbeiten' : 'Neuer Werbetraeger'}
      </DialogTitle>
      
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 2 }}>
          <TextField
            fullWidth
            label="Bezeichnung"
            value={form.bezeichnung}
            onChange={(e) => setForm({ ...form, bezeichnung: e.target.value })}
            disabled={saving}
            required
            placeholder="z.B. E-Mail Marketing"
          />

          <TextField
            fullWidth
            label="Kurzbezeichnung"
            value={form.kurz}
            onChange={(e) => setForm({ ...form, kurz: e.target.value.slice(0, 3).toUpperCase() })}
            disabled={saving}
            required
            placeholder="z.B. LST"
            helperText="Maximal 3 Zeichen"
            inputProps={{ maxLength: 3 }}
          />

          <TextField
            fullWidth
            label="Bemerkungen"
            multiline
            rows={3}
            value={form.bemerkungen}
            onChange={(e) => setForm({ ...form, bemerkungen: e.target.value })}
            disabled={saving}
          />

          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={form.active ? 'active' : 'inactive'}
              onChange={(e) => setForm({ ...form, active: e.target.value === 'active' })}
              label="Status"
              disabled={saving}
            >
              <MenuItem value="active">Aktiv</MenuItem>
              <MenuItem value="inactive">Inaktiv</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Abbrechen
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          sx={{ backgroundColor: '#003366' }}
          disabled={saving || !form.bezeichnung || !form.kurz}
        >
          {saving ? 'Speichern...' : 'Speichern'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
